import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TESTIDS } from "@/constants/testIds";
import { Repeat, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

export function GameHUD({ state, rulesetName, mode, humanSide }) {
  const captureA = state.captures.A;
  const captureB = state.captures.B;
  const isAActive = state.currentPlayer === "A" && state.status === "ACTIVE";
  const isBActive = state.currentPlayer === "B" && state.status === "ACTIVE";

  const playerLabel = (p) => {
    if (mode === "single") {
      return p === humanSide ? "You" : "Computer";
    }
    return p === "A" ? "Player 1" : "Player 2";
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
      <Card
        className={cn(
          "p-4 transition-colors",
          isAActive ? "border-primary ring-2 ring-primary/30" : "border-border"
        )}
        data-testid={TESTIDS.hudCapturesA}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">{playerLabel("A")}</p>
            <p className="font-display text-2xl font-semibold mt-0.5">A · bottom</p>
          </div>
          <Badge variant={isAActive ? "default" : "secondary"} className="rounded-full">
            {isAActive ? "Your turn" : "Waiting"}
          </Badge>
        </div>
        <div className="mt-3 flex items-end justify-between">
          <span className="text-xs text-muted-foreground">Captured</span>
          <span className="font-mono text-3xl font-bold">{captureA}</span>
        </div>
      </Card>

      <Card className="p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Ruleset</p>
            <p data-testid={TESTIDS.hudRuleset} className="font-medium mt-0.5">{rulesetName}</p>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Turn</p>
            <p data-testid={TESTIDS.hudTurn} className="font-mono font-semibold">{state.turnNumber}</p>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 text-xs">
          <Badge
            data-testid={TESTIDS.hudPhase}
            variant={state.phase === "SINGLETON" ? "destructive" : "outline"}
            className="rounded-full"
          >
            {state.phase === "SINGLETON" ? (
              <>
                <Sparkles className="h-3 w-3 mr-1" /> Singleton stage
              </>
            ) : (
              <>
                <Repeat className="h-3 w-3 mr-1" /> Normal play
              </>
            )}
          </Badge>
          <span data-testid={TESTIDS.hudCurrentPlayer} className="text-muted-foreground">
            {playerLabel(state.currentPlayer)} to play
          </span>
        </div>
      </Card>

      <Card
        className={cn(
          "p-4 transition-colors",
          isBActive ? "border-primary ring-2 ring-primary/30" : "border-border"
        )}
        data-testid={TESTIDS.hudCapturesB}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">{playerLabel("B")}</p>
            <p className="font-display text-2xl font-semibold mt-0.5">B · top</p>
          </div>
          <Badge variant={isBActive ? "default" : "secondary"} className="rounded-full">
            {isBActive ? "Their turn" : "Waiting"}
          </Badge>
        </div>
        <div className="mt-3 flex items-end justify-between">
          <span className="text-xs text-muted-foreground">Captured</span>
          <span className="font-mono text-3xl font-bold">{captureB}</span>
        </div>
      </Card>
    </div>
  );
}
